import React, { useEffect, useState } from "react";
import { Plus, Search, Rocket } from "lucide-react";
import {
  Agency,
  ProgramTemplate,
  isLaunchable,
  type LaunchUser,
} from "../../services/auditService";
import { StatusBadge } from "./AuditShared";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import Pagination from "../common/Pagination";
import NoDataFound from "../common/NoDataFound";
import StatusSwitch from "../common/StatusSwitch";
import LaunchProgramModal from "./LaunchProgramModal";
import { NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import ApprovalDot, { WorkflowState } from "../common/ApprovalDot";
import Toast, { ToastMessage } from "../common/Toast";

interface AuditProgramsProps {
  programs: ProgramTemplate[];
  agencies: Agency[];
  procedureCounts?: Record<number, number>;
  onAdd: () => void;
  onEdit: (program: ProgramTemplate) => void;
  onConfigure?: (program: ProgramTemplate) => void;
  /** Opens the status-dot workflow modal (submit/approve/reject/reopen). */
  onWorkflow?: (id: number) => void;
  /** Plan 04b-04 / DRC-07 — current logged-in user for the isLaunchable gate. */
  currentUser: LaunchUser | null;
  /** Plan 04b-04 / DRC-07 — parent refetches the Programs list after a launch. */
  onProgramLaunched?: () => void;
  /** Entry-point RBAC gate (ADT_PROGRAMS, view+modify) — disables the toolbar
   * Create button and the row Launch action so an unauthorized user is stopped
   * before opening a modal (dead-end), not only at the API. */
  canModify?: boolean;
  /** True while permission grants are loading — disables write entry points
   * (no tooltip) to avoid a flash of an enabled button. */
  permLoading?: boolean;
}

const SCOPE_BADGE: Record<string, string> = {
  "GRC-Common": "scope-badge scope-badge--grc-common",
  "Agency-Private": "scope-badge scope-badge--agency-private",
};

type StatusFilter = "all" | "active" | "inactive";

const AuditPrograms: React.FC<AuditProgramsProps> = ({
  programs,
  agencies,
  procedureCounts = {},
  onAdd,
  onConfigure,
  onWorkflow,
  currentUser,
  onProgramLaunched,
  canModify = true,
  permLoading = false,
}) => {
  const agencyName = (id?: number | null) =>
    agencies.find((a) => a.id === id)?.audit_agency_name || "—";

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Plan 04b-04 — modal state. When non-null, LaunchProgramModal is mounted for this template.
  const [launchFor, setLaunchFor] = useState<ProgramTemplate | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const q = searchTerm.trim().toLowerCase();
  const visible = programs.filter((p) => {
    if ((p.is_active ?? 1) === -1) return false;
    if (statusFilter === "active" && (p.is_active ?? 1) !== 1) return false;
    if (statusFilter === "inactive" && (p.is_active ?? 1) !== 0) return false;
    if (q) {
      const hay = `${p.audit_program_code ?? ""} ${p.audit_program_name ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, pageSize]);

  const totalRecords = visible.length;
  const paginated = visible.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <div className="netbody-header">
        {/* Status — iOS-style on/off switch (on = active) */}
        <StatusSwitch
          value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
          onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
          noun="programs"
          testId="SEL_program_status_filter"
        />
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Input
            type="text"
            placeholder="Search programs…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
            data-testid="INP_program_search"
          />
          <Button
            variant="fill"
            icon={Plus}
            onClick={onAdd}
            disabled={permLoading || !canModify}
            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
            data-testid="BTN_program_create"
          >
            Create
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Code</th>
                    <th className="table-list-th">Name</th>
                    <th className="table-list-th">Agency</th>
                    <th className="table-list-th">Cadence</th>
                    <th className="table-list-th table-list-cell--right">Procedures</th>
                    <th className="table-list-th">Scope</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                    <th className="table-list-th table-list-th--right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={10} className="table-list-cell">
                        <NoDataFound title="No Programs Found" message='Click "Create" to add one.' />
                      </td>
                    </tr>
                  )}
                  {paginated.map((prog, idx) => {
                    const isInactive = (prog.is_active ?? 1) === 0;
                    const scope = prog.ownership_scope ?? "GRC-Common";
                    return (
                      <tr
                        key={prog.id}
                        className="table-list-row"
                        onClick={() => { if (permLoading || !canModify) return; onConfigure && onConfigure(prog); }}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        aria-disabled={permLoading || !canModify}
                        style={permLoading || !canModify ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                        data-testid={`BTN_program_row_${prog.id}_open`}
                      >
                        <td
                          className="table-list-cell table-list-cell--dot"
                          onClick={(e) => { e.stopPropagation(); onWorkflow && onWorkflow(prog.id); }}
                          title="Workflow — view timeline & act"
                          data-testid={`BTN_program_row_${prog.id}_workflow`}
                        >
                          <ApprovalDot
                            state={((prog.workflow_state) || 'draft') as WorkflowState}
                            onClick={() => onWorkflow && onWorkflow(prog.id)}
                            title="Workflow — view timeline & act"
                          />
                        </td>
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          {prog.code
                            ? <span className="cell-code">{prog.code}</span>
                            : <span className="text-muted text-xs">—</span>}
                        </td>
                        <td className="table-list-cell">
                          <span className="table-list-cell-title">{prog.audit_program_name}</span>
                        </td>
                        <td className="table-list-cell">{agencyName(prog.agency_id)}</td>
                        <td className="table-list-cell">{prog.cadence || "—"}</td>
                        <td className="table-list-cell table-list-cell--right">
                          {procedureCounts[prog.id] ?? 0}
                        </td>
                        <td className="table-list-cell">
                          <span className={SCOPE_BADGE[scope] ?? "scope-badge"}>{scope}</span>
                        </td>
                        <td className="table-list-cell table-list-cell--status">
                          <StatusBadge
                            status={
                              prog.is_approved === 1
                                ? "Approved"
                                : isInactive
                                  ? "Inactive"
                                  : "Active"
                            }
                          />
                        </td>
                        <td className="table-list-cell table-list-cell--actions">
                          {isLaunchable(prog, currentUser) && (() => {
                            const noProcedures = (procedureCounts[prog.id] ?? 0) === 0;
                            return (
                            <button
                              type="button"
                              title={!canModify ? NOT_AUTHORISED_MSG : noProcedures ? "Program has no procedures — add procedures first" : "Launch this program template as a new run"}
                              aria-label="Launch program"
                              className="program-launch-btn"
                              disabled={permLoading || !canModify}
                              style={noProcedures ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                              data-testid={`BTN_program_row_${prog.id}_launch`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (noProcedures) {
                                  setToast({ type: 'error', message: 'Program has no procedures. Add procedures before launching.' });
                                  return;
                                }
                                setLaunchFor(prog);
                              }}
                            >
                              <Rocket size={14} />
                              <span>Launch</span>
                            </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="programs"
            />
          </div>
        </div>
      </div>

      {/* Plan 04b-04 / DRC-07 — launch cascade modal. */}
      <LaunchProgramModal
        isOpen={!!launchFor}
        onClose={() => setLaunchFor(null)}
        template={launchFor}
        currentUser={currentUser ?? { id: 0, roles: [] }}
        onLaunched={() => {
          setLaunchFor(null);
          onProgramLaunched?.();
        }}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default AuditPrograms;
